/**
 * Language Service - React Query Integration
 * Handles all language settings API communications
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import { Language } from '../types/admin';
import { AxiosError } from 'axios';

// ============================================
// API Response Types
// ============================================

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

type ApiLanguage = Language & {
  flag?: string;
};

// ============================================
// Query Keys
// ============================================

export const languageKeys = {
  all: ['languages'] as const,
  lists: () => [...languageKeys.all, 'list'] as const,
};

// ============================================
// API Functions
// ============================================

/**
 * Fetches all languages from the backend
 * Uses admin endpoint so inactive languages remain editable
 */
const fetchLanguages = async (): Promise<Language[]> => {
  try {
    const response = await axiosInstance.get<ApiResponse<ApiLanguage[]>>(
      '/v1/admin/settings/languages'
    );
    return (response.data.data || []).map(({ flag, flagUrl, ...language }) => ({
      ...language,
      flagUrl: flagUrl || flag,
    }));
  } catch (error) {
    console.error('Error fetching languages:', error);
    throw error;
  }
};

/**
 * Updates languages with file upload support
 * Uses FormData to handle both JSON data and file uploads
 */
const updateLanguages = async ({
  languages,
  flags,
}: {
  languages: Language[];
  flags: Record<string, File>;
}): Promise<Language[]> => {
  try {
    // Create FormData object
    const formData = new FormData();

    // Add languages as JSON string
    formData.append(
      'languages',
      JSON.stringify(
        languages.map(({ key, name, aliases, active }) => ({
          key,
          name,
          aliases: aliases ?? [],
          active,
        }))
      )
    );

    // Add flag files with the format flags[key]
    Object.entries(flags).forEach(([key, file]) => {
      formData.append(`flags[${key}]`, file);
    });

    // Send PUT request
    await axiosInstance.put<ApiResponse>('/v1/admin/settings/languages', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return fetchLanguages();
  } catch (error) {
    console.error('Error updating languages:', error);
    throw error;
  }
};

// ============================================
// React Query Hooks
// ============================================

/**
 * Hook to fetch all languages
 */
export const useLanguages = () => {
  return useQuery({
    queryKey: languageKeys.lists(),
    queryFn: fetchLanguages,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to update languages with optimistic updates
 */
export const useUpdateLanguages = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateLanguages,
    // Refetch on success
    onSuccess: (data) => {
      queryClient.setQueryData(languageKeys.lists(), data);
      queryClient.invalidateQueries({ queryKey: languageKeys.lists() });
    },
    onError: (error: AxiosError) => {
      console.error('Error updating languages:', error);
    },
  });
};
